/**
 * How many damages each view of the plan holds, for the view tabs (web and
 * app). Owner, 06.10.2026: „Bei den Ansichten muss schneller raus kommen, wo
 * Schäden registriert sind und wie viele … dem Statusfilter folgend.“
 *
 * A tab counts exactly what the drawing would show for that view: the
 * statuses in the filter, never „verworfen“ (it has no pin). The badge takes
 * the colour of the most urgent status among them, so a fresh report stands
 * out from known damage.
 */
import { OPEN_STATUSES, type AircraftView, type DamageStatus } from '@fsv/core';

/** The seven views in the order of the tabs. */
export const PLAN_VIEWS: readonly AircraftView[] = ['oben', 'unten', 'links', 'rechts', 'vorne', 'innen'];

/** What the plan shows when it opens: the open statuses, as the fleet counts them (owner, 06./07.10.2026). */
export const DEFAULT_PLAN_FILTER: readonly DamageStatus[] = OPEN_STATUSES;

/** Most urgent first: an unreviewed report, then known damage, then work in progress. */
export const STATUS_URGENCY: readonly DamageStatus[] = ['gemeldet', 'bekannt', 'in_reparatur', 'beobachtung', 'akzeptiert', 'behoben', 'verworfen'];

export interface ViewCount {
  readonly count: number;
  /** The most urgent status among the counted damages; null when there are none. */
  readonly top: DamageStatus | null;
}

export function countsByView(
  damages: readonly { view: AircraftView; status: DamageStatus }[],
  filter: readonly DamageStatus[],
): Record<AircraftView, ViewCount> {
  const out = Object.fromEntries(PLAN_VIEWS.map((v) => [v, { count: 0, top: null }])) as Record<AircraftView, { count: number; top: DamageStatus | null }>;
  for (const d of damages) {
    if (d.status === 'verworfen' || !filter.includes(d.status)) continue;
    const c = out[d.view];
    if (!c) continue;
    c.count += 1;
    if (c.top === null || STATUS_URGENCY.indexOf(d.status) < STATUS_URGENCY.indexOf(c.top)) c.top = d.status;
  }
  return out;
}

/**
 * The view the plan opens on when nobody asked for one: the one with the
 * most counted damages (the first in tab order on a tie), or „oben“ when
 * every view is empty.
 */
export function busiestView(counts: Record<AircraftView, ViewCount>): AircraftView {
  let best: AircraftView = 'oben';
  for (const v of PLAN_VIEWS) if (counts[v].count > counts[best].count) best = v;
  return best;
}
